package com.arenaproplay.admin.ui.components.admin

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.bodySMedium
import com.arenaproplay.admin.ui.theme.toRgbaString
import com.arenaproplay.admin.ui.theme.toRgbString
import com.arenaproplay.admin.ui.utils.sBorderRadius
import com.arenaproplay.admin.ui.utils.sPadding
import org.jetbrains.compose.web.css.*

val PAGE_SIZE_OPTIONS = listOf(25, 50, 100)

// ── Shared pager prev/next button ─────────────────────────────────────────────

/**
 * Reusable prev/next pager button used by all paged lists (Support, Chat, Social,
 * and the main PaginationBar).  Callers supply their own label strings so that
 * the layout semantics (arrow direction, locale) stay with the calling page.
 */
@Composable
fun PagerButton(label: String, onClick: () -> Unit, enabled: Boolean) {
    Box(
        Modifier
            .sBorderRadius(999).sPadding(8, 16)
            .cursor(if (enabled) Cursor.Pointer else Cursor.NotAllowed)
            .styleModifier {
                property("background", AppColors.Surface.toRgbString())
                property("border", "1px solid ${AppColors.Ink.toRgbaString(0.06f)}")
                property("box-shadow", "0 1px 2px ${AppColors.Ink.toRgbaString(0.03f)}")
                if (!enabled) property("opacity", "0.4")
            }
            .onClick { if (enabled) onClick() }
    ) {
        SpanText(label, Modifier.bodySMedium().color(AppColors.Ink))
    }
}

// ── Full pagination bar (page counter + page-size picker + prev/next) ─────────

@Composable
fun PaginationBar(
    page: Int,
    pageSize: Int,
    total: Int,
    onPrev: () -> Unit,
    onNext: () -> Unit,
    onPageSizeChange: ((Int) -> Unit)? = null
) {
    val from = if (total == 0) 0 else page * pageSize + 1
    val to = minOf((page + 1) * pageSize, total)
    Row(
        Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically
    ) {
        SpanText(
            "$from–$to из $total",
            Modifier.bodySMedium().color(AppColors.Muted)
        )
        Box(Modifier.weight(1f))
        if (onPageSizeChange != null) {
            SpanText("Строк", Modifier.bodySMedium().color(AppColors.Muted))
            HSpace(8)
            Row(
                Modifier
                    .sBorderRadius(999)
                    .styleModifier {
                        property("background", AppColors.Background.toRgbString())
                        property("border", "1px solid ${AppColors.Ink.toRgbaString(0.04f)}")
                        property("padding", "3px")
                    },
                verticalAlignment = Alignment.CenterVertically
            ) {
                PAGE_SIZE_OPTIONS.forEach { size ->
                    PageSizeChip(size, active = size == pageSize) { onPageSizeChange(size) }
                }
            }
            HSpace(14)
        }
        PagerButton("‹ Пред", onPrev, page > 0)
        HSpace(8)
        PagerButton("След ›", onNext, (page + 1) * pageSize < total)
    }
}

@Composable
private fun PageSizeChip(size: Int, active: Boolean, onClick: () -> Unit) {
    Box(
        Modifier
            .padding(leftRight = 12.px, topBottom = 5.px)
            .sBorderRadius(999)
            .cursor(Cursor.Pointer)
            .styleModifier {
                if (active) {
                    property("background", AppColors.Surface.toRgbString())
                    property("box-shadow", "0 1px 2px ${AppColors.Ink.toRgbaString(0.06f)}")
                }
                property("transition", "all 180ms ease")
            }
            .onClick { onClick() }
    ) {
        SpanText(
            size.toString(),
            Modifier.bodySMedium()
                .color(if (active) AppColors.Ink else AppColors.Muted)
                .styleModifier { if (active) property("font-weight", "600") }
        )
    }
}
